/**
 * PDP purchase column as a desktop console:
 * pin title/price, scroll the original purchase card, pin a proxy ATC.
 * Mobile keeps the original stacked flow via display:contents.
 */
(function () {
  const MQ = window.matchMedia('(min-width: 1024px)');
  const BODY_START = [
    'variant-selects',
    '.variant-picker',
    '.product-promo-callout',
    '.product-purchase-notice',
    '.product-coupon-banner',
    '.product-info-card',
    '.product-info-img-block',
    '.product-custom-banners',
    '.klaviyo-embed-anchor',
    '.product-form-wrapper',
    '.product-set-overview',
    '.product-set-inline',
    '.product-native-addons',
    '.product-avis-options',
    '.product-service-card',
    '.product-countdown',
    '.complementary-products',
    '.product__accordion',
    '.product__text',
    '.back-in-stock',
    '.product-selling-points',
    'quantity-input',
    '.product-bundle'
  ].join(',');
  const PASS_THROUGH = 'script, style, template, link';

  const instances = new WeakMap();

  const isElement = (node) => node && node.nodeType === Node.ELEMENT_NODE;

  const startsBody = (el) => el.matches(BODY_START) || Boolean(el.querySelector(BODY_START));

  const sourceBuy = (info) =>
    info.querySelector('.product-form__submit') || info.querySelector('.product-form__alert');

  const createDock = (foot) => {
    const existing = foot.querySelector('.product-info__dock-buy');
    if (existing) return existing;

    const dock = document.createElement('button');
    dock.type = 'button';
    dock.className = 'product-info__dock-buy';
    dock.innerHTML = '<span data-dock-label></span><span data-dock-sep aria-hidden="true">–</span><span data-dock-price></span>';
    foot.append(dock);
    return dock;
  };

  const syncDock = (info, dock) => {
    if (!dock) return;
    const source = sourceBuy(info);
    if (!source) {
      dock.hidden = true;
      return;
    }

    dock.hidden = false;
    dock.disabled = source.disabled;
    source.setAttribute('aria-hidden', 'true');
    source.setAttribute('tabindex', '-1');

    const label = ((source.querySelector('.btn-text > span') || {}).textContent || '').trim();
    const price = ((source.querySelector('product-buy-price') || {}).textContent || '').trim();
    dock.querySelector('[data-dock-label]').textContent = label;
    dock.querySelector('[data-dock-price]').textContent = price;
    dock.querySelector('[data-dock-sep]').hidden = !label || !price;
    dock.setAttribute('aria-label', [label, price].filter(Boolean).join(' – '));
  };

  const structure = (info) => {
    if (info.dataset.consoleStructured) {
      const scroll = info.querySelector('.product-info__scroll');
      if (scroll) {
        scroll.querySelectorAll('dialog').forEach((dialog) => info.append(dialog));
      }
      return {
        info,
        scroll,
        dock: info.querySelector('.product-info__dock-buy')
      };
    }

    const head = document.createElement('div');
    head.className = 'product-info__head';
    const body = document.createElement('div');
    body.className = 'product-info__body';
    const scroll = document.createElement('div');
    scroll.className = 'product-info__scroll';
    const foot = document.createElement('div');
    foot.className = 'product-info__foot';

    let zone = 'head';
    const overlays = [];
    const nodes = Array.from(info.childNodes);
    nodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) return;

      if (isElement(node) && node.matches('dialog')) {
        overlays.push(node);
        return;
      }

      if (!isElement(node) || node.matches(PASS_THROUGH)) {
        if (zone === 'head') head.append(node);
        else scroll.append(node);
        return;
      }

      if (zone === 'head' && startsBody(node)) zone = 'body';

      if (zone === 'head') head.append(node);
      else scroll.append(node);
    });

    const dock = createDock(foot);
    body.append(scroll);

    // Pin deal strip to final slot during first structure (before paint after defer).
    const deal = head.querySelector('[data-product-deal-strip]') || scroll.querySelector('[data-product-deal-strip]');
    if (deal) {
      if (MQ.matches) scroll.prepend(deal);
      else head.prepend(deal);
      deal.setAttribute('data-deal-placed', 'true');
    }

    info.append(head, body, foot, ...overlays);
    info.classList.add('product-info--console');
    info.dataset.consoleStructured = 'true';

    return { info, scroll, dock };
  };

  const syncEdge = (parts) => {
    const { info, scroll } = parts;
    if (!scroll || !MQ.matches) {
      info.classList.remove('has-overflow', 'is-away');
      scroll && scroll.removeAttribute('data-edge');
      return;
    }

    const max = scroll.scrollHeight - scroll.clientHeight;
    info.classList.toggle('has-overflow', max > 1);
    info.classList.toggle('is-away', scroll.scrollTop > 1);

    if (max <= 1) {
      scroll.removeAttribute('data-edge');
      return;
    }

    if (scroll.scrollTop <= 1) scroll.dataset.edge = 'start';
    else if (scroll.scrollTop >= max - 1) scroll.dataset.edge = 'end';
    else scroll.dataset.edge = 'middle';
  };

  const retargetSticky = (info) => {
    const sticky = document.querySelector('product-sticky-form');
    if (!sticky || !sticky.intersectionObserver) return;

    const form = document.getElementById(sticky.getAttribute('form'));
    const next = MQ.matches ? info : form;
    if (!next || sticky.scopeFrom === next) return;

    if (sticky.scopeFrom) sticky.intersectionObserver.unobserve(sticky.scopeFrom);
    sticky.scopeFrom = next;
    sticky.scopeFromPassed = false;
    sticky.intersectionObserver.observe(next);
  };

  const syncHeight = (parts) => {
    const { info, dock } = parts;
    const gallery = info.closest('.product') && info.closest('.product').querySelector('.product__gallery-container');

    if (!MQ.matches || !gallery) {
      info.style.height = '';
      if (!MQ.matches) {
        const source = sourceBuy(info);
        if (source) {
          source.removeAttribute('aria-hidden');
          source.removeAttribute('tabindex');
        }
        if (dock) dock.hidden = true;
      }
      retargetSticky(info);
      syncEdge(parts);
      return;
    }

    const next = `${gallery.offsetHeight}px`;
    if (info.style.height !== next) info.style.height = next;
    syncDock(info, dock);
    retargetSticky(info);
    syncEdge(parts);
  };

  const bind = (parts) => {
    if (instances.has(parts.info)) return;

    const onScroll = () => syncEdge(parts);
    const onWheel = (event) => {
      if (!MQ.matches) return;
      const max = parts.scroll.scrollHeight - parts.scroll.clientHeight;
      if (max <= 1) return;
      const atStart = parts.scroll.scrollTop <= 0 && event.deltaY < 0;
      const atEnd = parts.scroll.scrollTop >= max - 1 && event.deltaY > 0;
      if (atStart || atEnd) window.scrollBy(0, event.deltaY);
    };
    let frame = 0;
    const onResize = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        syncHeight(parts);
      });
    };

    parts.scroll.addEventListener('scroll', onScroll, { passive: true });
    parts.scroll.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);

    if (parts.dock) {
      parts.dock.addEventListener('click', () => {
        const source = sourceBuy(parts.info);
        if (source && !source.disabled) source.click();
      });
    }

    const observers = [];
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(onResize);
      const gallery = parts.info.closest('.product') && parts.info.closest('.product').querySelector('.product__gallery-container');
      if (gallery) ro.observe(gallery);
      observers.push(ro);
    }

    const mo = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.target !== parts.info) return;
        record.addedNodes.forEach((node) => {
          if (!isElement(node)) return;
          if (node.matches('.product-info__head, .product-info__body, .product-info__foot, .product-deal-strip')) return;
          if (parts.scroll.contains(node) || parts.info.querySelector('.product-info__head')?.contains(node) || parts.info.querySelector('.product-info__foot')?.contains(node)) return;
          parts.scroll.append(node);
        });
      });
      onResize();
    });
    mo.observe(parts.info, { childList: true });
    mo.observe(parts.scroll, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'disabled'] });
    observers.push(mo);

    instances.set(parts.info, { onResize, observers });
    syncHeight(parts);
    requestAnimationFrame(() => syncHeight(parts));
  };

  const init = (info) => {
    if (!info) return;
    bind(structure(info));
  };

  const boot = (root) => {
    (root || document).querySelectorAll('[data-product-info-console]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
